import React, { useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Menu from '@mui/material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { GridColDef } from '@mui/x-data-grid';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useTenantNav } from '../hooks/useTenantNav';
import { portfoliosApi, PortfolioListItem, StrategicCategory, strategicCategoriesApi } from '../api/ppm';
import { canEditPage } from '../utils/rbac';
import { titleCase } from '../utils/format';
import apiClient, { usersApi, DbUser } from '../api/client';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import LinearProgress from '@mui/material/LinearProgress';
import { PageHeader } from './layout/PageHeader';
import { StatCard } from './layout/StatCard';
import { PageLoader } from './PageLoader';
import { TableCard } from './layout/TableCard';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { ChartCard } from './charts/ChartCard';
import { NexusBarChart, NexusDonut } from './charts/NexusCharts';
import { tokens, chartColor } from '../theme/tokens';
import { computeFinance, formatCurrencyShort, formatSignedCurrencyShort } from '../utils/finance';

const statusColor = (status: string) => {
  const s = (status || '').toUpperCase();
  if (s.includes('ACTIVE')) return 'success';
  if (s.includes('PLAN')) return 'info';
  if (s.includes('HOLD')) return 'warning';
  if (s.includes('COMP')) return 'default';
  if (s.includes('ARCH')) return 'default';
  return 'default';
};

const Portfolios: React.FC = () => {
  const { navigate } = useTenantNav();
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'portfolios');
  const [rows, setRows] = useState<PortfolioListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<PortfolioListItem | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    status: 'ACTIVE',
    ownerId: '',
    strategicCategoryIds: [] as string[],
    totalBudget: '',
    startDate: '',
    endDate: '',
  });

  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState<{ severity: 'success' | 'error'; message: string }>({ severity: 'success', message: '' });

  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsRow, setActionsRow] = useState<PortfolioListItem | null>(null);
  const actionsOpen = Boolean(actionsAnchorEl);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [topPanel, setTopPanel] = useState<null | 'portfolios' | 'budget' | 'spent' | 'cost'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);
  const [companyBudget, setCompanyBudget] = useState<number | null>(null);
  const companyRemainingBudget = useMemo(() => {
    if (companyBudget == null) return null;
    const used = (rows || []).reduce((s, p: any) => s + Number(p?.totalBudget || 0), 0);
    return Number(companyBudget || 0) - used;
  }, [companyBudget, rows]);

  const [categories, setCategories] = useState<StrategicCategory[]>([]);
  const [me, setMe] = useState<DbUser | null>(null);

  useEffect(() => {
    usersApi.me().then((r) => setMe((r.data || null) as any)).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [res, cb, cats] = await Promise.all([
          portfoliosApi.getAll(),
          apiClient.get('/financials/company-budget').catch(() => ({ data: null })),
          strategicCategoriesApi.getAll().catch(() => ({ data: [] as StrategicCategory[] })),
        ]);
        if (!mounted) return;
        setRows(res.data || []);
        setCompanyBudget(cb?.data?.totalBudget != null ? Number(cb.data.totalBudget) : null);
        setCategories((cats.data || []) as any);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load portfolios');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '',
      description: '',
      status: 'ACTIVE',
      ownerId: me?.id || '',
      strategicCategoryIds: [],
      totalBudget: '',
      startDate: '',
      endDate: '',
    });
    setEditOpen(true);
  };

  const openEdit = (p: PortfolioListItem) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      description: (p.description as any) || '',
      status: (p.status as any) || 'ACTIVE',
      ownerId: (p.ownerId as any) || 'demo_user_pm',
      strategicCategoryIds: (p.strategicCategoryIds as any) || [],
      totalBudget: p.totalBudget != null ? String(p.totalBudget) : '',
      startDate: p.startDate ? String(p.startDate).slice(0, 10) : '',
      endDate: p.endDate ? String(p.endDate).slice(0, 10) : '',
    });
    setEditOpen(true);
  };

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await portfoliosApi.getAll();
      setRows(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    try {
      if (!form.name.trim()) {
        setToast({ severity: 'error', message: 'Name is required' });
        setToastOpen(true);
        return;
      }

      // Client-side guardrail (backend enforces too)
      const desiredBudget = Number(form.totalBudget || 0);
      if (!Number.isFinite(desiredBudget) || desiredBudget < 0) {
        setToast({ severity: 'error', message: 'Total Budget must be a valid number' });
        setToastOpen(true);
        return;
      }
      if (companyBudget != null) {
        const usedOther = (rows || []).reduce((s, p: any) => {
          if (editing && String(p.id) === String(editing.id)) return s;
          return s + Number(p?.totalBudget || 0);
        }, 0);
        const remaining = Number(companyBudget) - usedOther;
        if (desiredBudget > remaining + 1e-9) {
          setToast({ severity: 'error', message: `Company budget exceeded. Remaining ${money.format(Math.max(0, remaining))}.` });
          setToastOpen(true);
          return;
        }
      }

      const numOrUndef = (v: string) => (v.trim() === '' ? undefined : Number(v));
      const dateOrUndef = (v: string) => (v.trim() === '' ? undefined : new Date(v));

      const payload: any = {
        name: form.name.trim(),
        description: form.description.trim() ? form.description.trim() : undefined,
        status: form.status || 'ACTIVE',
        strategicCategoryIds: Array.isArray(form.strategicCategoryIds) ? form.strategicCategoryIds : [],
        totalBudget: numOrUndef(form.totalBudget),
        startDate: dateOrUndef(form.startDate),
        endDate: dateOrUndef(form.endDate),
      };

      // Owner is always the authenticated user (backend enforces too).
      if (!editing && me?.id) {
        payload.Owner = { connect: { id: me.id } };
      }

      if (editing) {
        await portfoliosApi.update(editing.id, payload);
        setToast({ severity: 'success', message: 'Portfolio updated' });
      } else {
        await portfoliosApi.create(payload);
        setToast({ severity: 'success', message: 'Portfolio created' });
      }

      setToastOpen(true);
      setEditOpen(false);
      await refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Save failed' });
      setToastOpen(true);
    }
  };

  const remove = async (p: PortfolioListItem) => {
    try {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(`Delete portfolio "${p.name}"? This cannot be undone.`);
      if (!ok) return;
      await portfoliosApi.remove(p.id);
      setToast({ severity: 'success', message: 'Portfolio deleted' });
      setToastOpen(true);
      await refresh();
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message || e?.message || 'Delete failed' });
      setToastOpen(true);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((p) => {
      const name = (p.name || '').toLowerCase();
      const align = (Array.isArray((p as any)?.strategicAlignments) ? (p as any).strategicAlignments.join(' ') : (p.strategicAlignment || '')).toLowerCase();
      const owner = (p.Owner?.email || p.Owner?.displayName || '').toLowerCase();
      return name.includes(q) || align.includes(q) || owner.includes(q);
    });
  }, [rows, search]);

  const summary = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((p) => String(p.status || '').toUpperCase().includes('ACTIVE')).length;
    const totalBudget = rows.reduce((acc, p) => acc + Number(p.totalBudget || 0), 0);
    const totalAllocated = rows.reduce((acc, p: any) => {
      const proj = Array.isArray(p?.Projects) ? p.Projects : [];
      const derived = proj.reduce((s: number, x: any) => s + Number(x?.budgetedCost || 0), 0);
      return acc + (derived || Number(p.totalSpent || 0));
    }, 0);
    const totalSpend = rows.reduce((acc, p: any) => {
      const proj = Array.isArray(p?.Projects) ? p.Projects : [];
      const derived = proj.reduce((s: number, x: any) => s + Number(x?.actualCost || 0), 0);
      return acc + derived;
    }, 0);
    const company = companyBudget != null ? Number(companyBudget) : totalBudget;
    // Two different "unallocated" concepts:
    // - to portfolios: company − sum(portfolio budgets)
    // - to projects: company − sum(project allocated budgets)
    const unallocatedToPortfolios = company - totalBudget; // can be negative if portfolios exceed company budget
    const unallocatedToProjects = company - totalAllocated; // can be negative if projects exceed company budget
    return { total, active, totalBudget, totalAllocated, totalSpend, companyBudget: company, unallocatedToPortfolios, unallocatedToProjects };
  }, [rows, companyBudget]);

  const portfolioFinance = useMemo(
    () =>
      computeFinance({
        budget: Number(summary.totalBudget || 0),
        allocated: Number((summary as any).totalAllocated || 0),
        spent: Number((summary as any).totalSpend || 0),
      }),
    [summary],
  );

  const topByBudget = useMemo(() => {
    return rows
      .slice()
      .sort((a, b) => Number(b.totalBudget || 0) - Number(a.totalBudget || 0))
      .filter((p) => Number(p.totalBudget || 0) > 0);
  }, [rows]);

  const topBySpent = useMemo(() => {
    return rows
      .slice()
      .map((p: any) => {
        const proj = Array.isArray(p?.Projects) ? p.Projects : [];
        const derivedAllocated = proj.reduce((s: number, x: any) => s + Number(x?.budgetedCost || 0), 0) || Number(p.totalSpent || 0);
        return { ...p, __derivedSpent: derivedAllocated };
      })
      .sort((a: any, b: any) => Number(b.__derivedSpent || 0) - Number(a.__derivedSpent || 0))
      .filter((p: any) => Number(p.__derivedSpent || 0) > 0);
  }, [rows]);

  const topByCost = useMemo(() => {
    return rows
      .slice()
      .map((p: any) => {
        const proj = Array.isArray(p?.Projects) ? p.Projects : [];
        const derivedCost = proj.reduce((s: number, x: any) => s + Number(x?.actualCost || 0), 0);
        return { ...p, __derivedCost: derivedCost };
      })
      .sort((a: any, b: any) => Number(b.__derivedCost || 0) - Number(a.__derivedCost || 0))
      .filter((p: any) => Number(p.__derivedCost || 0) > 0);
  }, [rows]);

  const money = useMemo(() => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
  }, []);

  const statusBreakdown = useMemo(() => {
    const map = new Map<string, number>();
    (rows || []).forEach((p: any) => {
      const s = String(p.status || 'UNKNOWN').toUpperCase();
      map.set(s, (map.get(s) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [rows]);

  const budgetVsSpent = useMemo(() => {
    const budget = Number(summary.totalBudget || 0);
    const spent = Number((summary as any).totalAllocated || 0);
    return [
      { name: 'Budget', value: budget },
      { name: 'Allocated', value: spent },
      { name: 'Remaining', value: Math.max(0, budget - spent) },
    ];
  }, [summary]);

  const spendRateBuckets = useMemo(() => {
    const buckets = [
      { name: '0–25%', value: 0 },
      { name: '25–50%', value: 0 },
      { name: '50–75%', value: 0 },
      { name: '75–100%', value: 0 },
      { name: '100%+', value: 0 },
    ];
    (rows || []).forEach((p: any) => {
      const b = Number(p.totalBudget || 0);
      const proj = Array.isArray(p?.Projects) ? p.Projects : [];
      const s = proj.reduce((acc: number, x: any) => acc + Number(x?.budgetedCost || 0), 0) || Number(p.totalSpent || 0);
      if (b <= 0) return;
      const pct = (s / b) * 100;
      if (pct < 25) buckets[0].value += 1;
      else if (pct < 50) buckets[1].value += 1;
      else if (pct < 75) buckets[2].value += 1;
      else if (pct <= 100) buckets[3].value += 1;
      else buckets[4].value += 1;
    });
    return buckets;
  }, [rows]);

  const cols: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Portfolio',
      flex: 1,
      minWidth: 320,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => navigate(`/portfolios/${params.row.id}`)}
          sx={{
            textTransform: 'none',
            fontWeight: 650,
            color: 'text.primary',
            justifyContent: 'flex-start',
            px: 0,
            minWidth: 0,
            '&:hover': { backgroundColor: 'transparent', color: 'text.primary' },
          }}
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? titleCase(String(params.value)) : '—'}
          color={statusColor(params.value) as any}
          variant="outlined"
          sx={{ height: 22, fontWeight: 700, letterSpacing: 0.2 }}
        />
      ),
    },
    {
      field: 'strategicAlignment',
      headerName: 'Strategic Alignment',
      flex: 1,
      minWidth: 260,
      valueGetter: (params) => {
        const a = (params.row as any)?.strategicAlignments;
        if (Array.isArray(a) && a.length) return a.join(', ');
        return params.row?.strategicAlignment || '—';
      },
    },
    {
      field: 'projects',
      headerName: 'Projects',
      width: 120,
      valueGetter: (params) => params.row?._count?.Projects ?? params.row?.Projects?.length ?? 0,
    },
    {
      field: 'members',
      headerName: 'Members',
      width: 120,
      valueGetter: (params) => (params.row as any)?.membersCount ?? params.row?._count?.PortfolioMembers ?? 0,
    },
    {
      field: 'owner',
      headerName: 'Spend %',
      width: 190,
      valueGetter: (params) => {
        const b = Number(params.row?.totalBudget || 0);
        const proj = Array.isArray(params.row?.Projects) ? params.row.Projects : [];
        const s = proj.reduce((acc: number, x: any) => acc + Number(x?.budgetedCost || 0), 0) || Number(params.row?.totalSpent || 0);
        if (!b || b <= 0) return 0;
        return Math.round((s / b) * 100);
      },
      sortComparator: (a: any, b: any) => Number(a || 0) - Number(b || 0),
      renderCell: (params) => {
        const pct = Number(params.value || 0);
        return (
          <Box sx={{ width: '100%', pr: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              Spend {pct}%
            </Typography>
            <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, pct))} sx={{ height: 8, borderRadius: 999 }} />
          </Box>
        );
      },
    },
    ...(canEdit
      ? ([
          {
            field: 'actions',
            headerName: 'Admin',
            width: 90,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setActionsAnchorEl(e.currentTarget);
                  setActionsRow(params.row);
                }}
                aria-label="Actions"
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ),
          } as GridColDef,
        ] as GridColDef[])
      : []),
  ];

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="Portfolios"
        subtitle="Portfolio health, budgets, projects, and strategic alignment."
        actions={
          <>
            <TextField
              size="small"
              placeholder="Search portfolios (name, owner, alignment)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: '100%', sm: 520 } }}
            />
            {canEdit ? (
              <Button variant="contained" onClick={openCreate}>
                Add
              </Button>
            ) : null}
          </>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Portfolios"
            value={summary.total}
            subtitle={`${summary.active} active`}
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'portfolios' ? null : 'portfolios'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total portfolio budget"
            value={money.format(summary.totalBudget)}
            subtitle="Sum of portfolio budgets"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'budget' ? null : 'budget'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Allocated"
            value={money.format((summary as any).totalAllocated)}
            subtitle="Allocated to projects"
            accent={tokens.color.success}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'spent' ? null : 'spent'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Variance"
            value={formatSignedCurrencyShort(portfolioFinance.variance)}
            subtitle={`${portfolioFinance.varianceLabel} • spent ${money.format(Number((summary as any).totalSpend || 0))}`}
            tone={portfolioFinance.tone}
            accent={
              portfolioFinance.tone === 'favorable'
                ? tokens.color.success
                : portfolioFinance.tone === 'unfavorable'
                  ? tokens.color.danger
                  : tokens.color.warning
            }
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'cost' ? null : 'cost'));
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={!!topPanel} unmountOnExit>
            <TableCard
              title={topPanel === 'portfolios' ? 'Portfolios' : topPanel === 'budget' ? 'Top budgets' : topPanel === 'spent' ? 'Top allocations' : 'Top spend'}
              actions={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                    {showAllPanel ? 'Show less' : 'Show all'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setTopPanel(null)} sx={{ borderRadius: 2 }}>
                    Close
                  </Button>
                </Stack>
              }
            >
              {topPanel === 'portfolios' ? (
                <Stack spacing={1}>
                  {(rows || []).slice(0, showAllPanel ? 1000 : 5).map((p) => (
                    <Box
                      key={p.id}
                      sx={{ p: 1, borderRadius: 2, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                      onClick={() => {
                        setActionsRow(p);
                        setDetailsOpen(true);
                      }}
                    >
                      <Typography sx={{ fontWeight: 650 }} noWrap>
                        {p.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {p.status ? titleCase(String(p.status)) : '—'} • budget {money.format(Number(p.totalBudget || 0))} • allocated{' '}
                        {money.format(
                          (() => {
                            const proj = Array.isArray((p as any)?.Projects) ? (p as any).Projects : [];
                            const derived = proj.reduce((s: number, x: any) => s + Number(x?.budgetedCost || 0), 0);
                            return derived || Number((p as any)?.totalSpent || 0);
                          })(),
                        )}
                        {' '}
                        • spend{' '}
                        {money.format(
                          (() => {
                            const proj = Array.isArray((p as any)?.Projects) ? (p as any).Projects : [];
                            return proj.reduce((s: number, x: any) => s + Number(x?.actualCost || 0), 0);
                          })(),
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : null}

              {topPanel === 'budget' ? (
                topByBudget.length ? (
                  <Stack spacing={1}>
                    {topByBudget.slice(0, showAllPanel ? 1000 : 5).map((p) => (
                      <Box
                        key={p.id}
                        sx={{ p: 1, borderRadius: 2, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                        onClick={() => {
                          setActionsRow(p);
                          setDetailsOpen(true);
                        }}
                      >
                        <Typography sx={{ fontWeight: 650 }} noWrap>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          budget {money.format(Number(p.totalBudget || 0))} • allocated{' '}
                          {money.format(
                            (() => {
                              const proj = Array.isArray((p as any)?.Projects) ? (p as any).Projects : [];
                              const derived = proj.reduce((s: number, x: any) => s + Number(x?.budgetedCost || 0), 0);
                              return derived || Number((p as any)?.totalSpent || 0);
                            })(),
                          )}
                          {' '}
                          • spend{' '}
                          {money.format(
                            (() => {
                              const proj = Array.isArray((p as any)?.Projects) ? (p as any).Projects : [];
                              return proj.reduce((s: number, x: any) => s + Number(x?.actualCost || 0), 0);
                            })(),
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info" variant="outlined">
                    No portfolios have budgets set yet.
                  </Alert>
                )
              ) : null}

              {topPanel === 'spent' ? (
                topBySpent.length ? (
                  <Stack spacing={1}>
                    {topBySpent.slice(0, showAllPanel ? 1000 : 5).map((p) => (
                      <Box
                        key={p.id}
                        sx={{ p: 1, borderRadius: 2, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                        onClick={() => {
                          setActionsRow(p);
                          setDetailsOpen(true);
                        }}
                      >
                        <Typography sx={{ fontWeight: 650 }} noWrap>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          allocated {money.format(Number((p as any).__derivedSpent ?? 0))} • budget {money.format(Number(p.totalBudget || 0))} • spend{' '}
                          {money.format(
                            (() => {
                              const proj = Array.isArray((p as any)?.Projects) ? (p as any).Projects : [];
                              return proj.reduce((s: number, x: any) => s + Number(x?.actualCost || 0), 0);
                            })(),
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info" variant="outlined">
                    No portfolios have allocations yet.
                  </Alert>
                )
              ) : null}

              {topPanel === 'cost' ? (
                topByCost.length ? (
                  <Stack spacing={1}>
                    {topByCost.slice(0, showAllPanel ? 1000 : 5).map((p: any) => (
                      <Box
                        key={p.id}
                        sx={{ p: 1, borderRadius: 2, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                        onClick={() => {
                          setActionsRow(p);
                          setDetailsOpen(true);
                        }}
                      >
                        <Typography sx={{ fontWeight: 650 }} noWrap>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          spend {money.format(Number(p.__derivedCost || 0))} • allocated{' '}
                          {money.format(
                            (() => {
                              const proj = Array.isArray(p?.Projects) ? p.Projects : [];
                              return proj.reduce((s: number, x: any) => s + Number(x?.budgetedCost || 0), 0);
                            })(),
                          )}{' '}
                          • budget {money.format(Number(p.totalBudget || 0))}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info" variant="outlined">
                    No portfolios have spend yet.
                  </Alert>
                )
              ) : null}
            </TableCard>
          </Collapse>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <ChartCard title="Budget vs allocated" subtitle="Portfolio rollup" height={260}>
            <NexusBarChart
              data={budgetVsSpent}
              xKey="name"
              series={[{ key: 'value', name: 'Amount', color: tokens.color.accent }]}
              valueFormatter={(v) => formatCurrencyShort(v)}
              height={232}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Status mix" subtitle="Portfolios by status" height={260}>
            <NexusDonut
              data={statusBreakdown.map((d, i) => ({ name: titleCase(d.name), value: d.value, color: chartColor(i) }))}
              valueFormatter={(v) => String(v)}
              height={200}
              centerLabel="Total"
              centerValue={String(summary.total)}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Spend rate distribution" subtitle="Spent / budget per portfolio" height={260}>
            <NexusBarChart
              data={spendRateBuckets}
              xKey="name"
              series={[{ key: 'value', name: 'Portfolios', color: tokens.color.accent }]}
              valueFormatter={(v) => String(v)}
              height={232}
            />
          </ChartCard>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ borderColor: tokens.color.border }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Portfolio list
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click a row for details. Use the menu for admin actions.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {loading ? <CircularProgress size={18} /> : null}
              <Button size="small" variant="outlined" onClick={refresh} sx={{ borderRadius: 2 }}>
                Refresh
              </Button>
            </Stack>
          </Box>
          <Divider sx={{ my: 1.25 }} />
          <div style={{ height: 560, width: '100%' }}>
            <ResizableDataGrid
              storageKey="gridWidths.portfolios"
              enableDensityControls
              rows={filtered}
              columns={cols}
              getRowId={(r) => r.id}
              disableRowSelectionOnClick
              onRowClick={(p) => {
                setActionsRow(p.row);
                setDetailsOpen(true);
              }}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': { backgroundColor: tokens.color.surfaceMuted },
                '& .MuiDataGrid-row:hover': { backgroundColor: tokens.color.surfaceMuted },
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Portfolio' : 'Create Portfolio'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            label="Status"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            select
            fullWidth
          >
            {['PLANNING', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'ARCHIVED'].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <Alert severity="info" variant="outlined">
            Owner is set automatically to <b>{me?.username || me?.email || 'you'}</b>.
          </Alert>
          <TextField
            select
            label="Strategic Alignment"
            value={form.strategicCategoryIds}
            onChange={(e) => setForm((f) => ({ ...f, strategicCategoryIds: (e.target.value as any) || [] }))}
            fullWidth
            SelectProps={{
              multiple: true,
              renderValue: (selected) => {
                const ids = Array.isArray(selected) ? selected : [];
                const labels = ids
                  .map((id) => categories.find((c) => String(c.id) === String(id))?.name || String(id))
                  .filter(Boolean);
                return labels.length ? labels.join(', ') : '—';
              },
            }}
            helperText="Pick one or more strategic themes this portfolio supports."
          >
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Total Budget"
              value={form.totalBudget}
              onChange={(e) => setForm((f) => ({ ...f, totalBudget: e.target.value }))}
              fullWidth
              helperText={
                companyRemainingBudget != null
                  ? `Company remaining budget (before this portfolio): ${money.format(companyRemainingBudget)}`
                  : 'Company budget remaining will appear here once configured.'
              }
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Start Date"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={form.endDate}
              onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toastOpen} autoHideDuration={2500} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>

      <Menu
        anchorEl={actionsAnchorEl}
        open={actionsOpen}
        onClose={() => {
          setActionsAnchorEl(null);
          setActionsRow(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionsRow) openEdit(actionsRow);
            setActionsAnchorEl(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionsRow) remove(actionsRow);
            setActionsAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Portfolio</DialogTitle>
        <DialogContent>
          {actionsRow ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={actionsRow.status ? titleCase(String(actionsRow.status)) : '—'}
                  color={statusColor(actionsRow.status) as any}
                  variant="outlined"
                  sx={{ height: 22, fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  label={actionsRow.Owner?.email || actionsRow.Owner?.displayName || '—'}
                  variant="outlined"
                  sx={{ height: 22, fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  label={`${actionsRow._count?.Projects ?? actionsRow.Projects?.length ?? 0} projects`}
                  variant="outlined"
                  sx={{ height: 22, fontWeight: 700 }}
                />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 650 }}>
                {actionsRow.name}
              </Typography>
              {actionsRow.description ? <Typography variant="body2">{actionsRow.description}</Typography> : null}
              <Typography variant="body2" color="text.secondary">
                {Array.isArray((actionsRow as any)?.strategicAlignments) && (actionsRow as any).strategicAlignments.length
                  ? `Alignment: ${(actionsRow as any).strategicAlignments.join(', ')}`
                  : actionsRow.strategicAlignment
                    ? `Alignment: ${actionsRow.strategicAlignment}`
                    : null}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {actionsRow ? (
            <Button variant="outlined" onClick={() => navigate(`/portfolios/${actionsRow.id}`)}>
              Open
            </Button>
          ) : null}
          {canEdit && actionsRow ? (
            <>
              <Button variant="outlined" onClick={() => openEdit(actionsRow)}>
                Edit
              </Button>
              <Button color="error" variant="outlined" onClick={() => remove(actionsRow)}>
                Delete
              </Button>
            </>
          ) : null}
          <Button variant="contained" onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </PageLoader>
  );
};

export default Portfolios;


